import type { PricingSeason } from "@/types";

/**
 * 상품 상세 "요금 상세"의 기간별 요금표(10-16 C안).
 *
 * 세부 상품마다 **대표 요금 1칸**(기본 N일 요금)을 기간별로 비교한다 — 시즌 기간마다
 * 그 기간의 대표 요금, 마지막에 시즌 밖("그 외 기간")의 기본 요금. 결제 계산
 * (`getSeasonalDailyRates`)과 같은 rate 행을 읽는다: (daily, sub, tier min_days) 행의
 * 일 단가 × N일. 행이 없는 세부 상품은 그 기간에도 기본 요금이다(부분 재정의).
 *
 * 금액이 같은 기간은 한 줄로 묶고("3/1 ~ 6/30 · 10/1 ~ 10/31"), 세부 상품끼리 금액이
 * 모두 같으면 `uniform` — 화면은 기간 | 금액 한 줄씩, 다르면 기간 머리 줄 아래 세부
 * 상품 줄(같은 금액끼리 묶음).
 */

export interface SeasonTableSub {
  /** `sub_display_order` — v1 상품(세부 상품 없음)은 null */
  order: number | null;
  name: string;
  /** 대표 요금이 몇 일 요금인가 — 기본+추가일 상품의 baseDays, 그 외 1 */
  unitDays: number;
  /** 시즌 rate 를 찾을 티어(min_days) — 기본+추가일은 unitDays, 사다리는 첫 티어 */
  tierMinDays: number;
  /** 시즌 밖 대표 요금(마크업 반영, 원 통화) */
  basePrice: number;
}

export interface SeasonTablePeriod {
  /** 묶인 기간들 — 시작일 순. 빈 배열 = "그 외 기간" */
  ranges: Array<{ start: string; end: string; yearly: boolean; weeklyDays?: number[] | null }>;
  /** subs 와 같은 순서의 금액 */
  prices: number[];
}

export interface SeasonTable {
  periods: SeasonTablePeriod[];
  /** 모든 기간에서 세부 상품 금액이 같다(세부 상품 1개 포함) */
  uniform: boolean;
  /** 표의 최저 금액 — 헤드라인 "부터" */
  minPrice: number;
}

function seasonUnitPrice(
  season: PricingSeason,
  sub: SeasonTableSub,
): number | null {
  const rate = season.rates.find(
    (r) =>
      r.pricing_type === "daily" &&
      (r.sub_display_order ?? null) === sub.order &&
      (r.min_days ?? 1) === sub.tierMinDays,
  );
  const perDay = rate ? Number(rate.price) : NaN;
  return Number.isFinite(perDay) && perDay > 0 ? perDay * sub.unitDays : null;
}

/** 시즌이 없거나 어느 세부 상품에도 rate 가 없으면 null(표 생략). */
export function buildSeasonTable(
  subs: SeasonTableSub[],
  seasons: PricingSeason[] | null | undefined,
): SeasonTable | null {
  if (!seasons?.length || subs.length === 0) return null;

  const sorted = [...seasons].sort((a, b) =>
    a.start_date.slice(5).localeCompare(b.start_date.slice(5)) ||
    a.start_date.localeCompare(b.start_date),
  );

  const groups: SeasonTablePeriod[] = [];
  let anyRate = false;
  for (const season of sorted) {
    const own = subs.map((sub) => seasonUnitPrice(season, sub));
    if (own.every((p) => p == null)) continue;
    anyRate = true;
    const prices = own.map((p, i) => p ?? subs[i].basePrice);
    const range = {
      start: season.start_date,
      end: season.end_date,
      yearly: season.repeat_type === "yearly",
      ...(season.repeat_type === "weekly" ? {weeklyDays: season.weekly_days} : {}),
    };
    const same = groups.find((g) => g.prices.every((p, i) => p === prices[i]));
    if (same) same.ranges.push(range);
    else groups.push({ ranges: [range], prices });
  }
  if (!anyRate) return null;

  groups.push({ ranges: [], prices: subs.map((s) => s.basePrice) });

  const uniform = groups.every((g) => g.prices.every((p) => p === g.prices[0]));
  const minPrice = Math.min(
    ...groups.flatMap((g) => g.prices.filter((p) => p > 0)),
  );
  return { periods: groups, uniform, minPrice };
}

/** "3/1 ~ 6/30" — 매년 반복은 월/일만, 일회성은 연도까지("2026/12/20 ~ 2027/1/5"). */
export function formatSeasonRange(range: {
  start: string;
  end: string;
  yearly: boolean;
  weeklyDays?: number[] | null;
}, locale: string | number = "en"): string {
  const fmt = (value: string) => {
    const [y, m, d] = value.split("-").map(Number);
    return range.yearly ? `${m}/${d}` : `${y}/${m}/${d}`;
  };
  const period = `${fmt(range.start)} ~ ${fmt(range.end)}`;
  if (!range.weeklyDays?.length) return period;
  const formatter = new Intl.DateTimeFormat(typeof locale === "string" ? locale : "en", { weekday: "short", timeZone: "UTC" });
  return `${period} (${range.weeklyDays.map((day) => formatter.format(new Date(Date.UTC(2026, 0, 4 + day)))).join(" · ")})`;
}

/** 한 기간 안에서 금액이 같은 세부 상품을 한 줄로 — 첫 등장 순서 유지. */
export function groupSubsByPrice(
  subs: SeasonTableSub[],
  prices: number[],
): Array<{ names: string[]; price: number }> {
  const rows: Array<{ names: string[]; price: number }> = [];
  subs.forEach((sub, i) => {
    const row = rows.find((r) => r.price === prices[i]);
    if (row) row.names.push(sub.name);
    else rows.push({ names: [sub.name], price: prices[i] });
  });
  return rows;
}
